import { FC, useContext, createContext, useState } from "react";
import { BarChart3Icon, Loader2Icon, ChevronDownIcon, ChevronUpIcon, PlayIcon, LineChartIcon, TableIcon, HashIcon, PieChartIcon, CheckCircle2Icon, type LucideIcon } from "lucide-react";
import AnalysisRunner from "@components/analysis/AnalysisRunner.component";
import { useAnalysis } from "@models/analysis";

// Map visualization types to appropriate icons
const getChartIcon = (vizType?: string): LucideIcon => {
  switch (vizType) {
    case 'BAR':
      return BarChart3Icon;
    case 'LINE':
      return LineChartIcon;
    case 'TABLE':
    case 'PIVOT_TABLE':
      return TableIcon;
    case 'SINGLE_VALUE':
      return HashIcon;
    case 'PIE':
      return PieChartIcon;
    default:
      return BarChart3Icon;
  }
};

// Context for tool call handlers - exported so thread.tsx can provide the value
export interface ToolCallHandlers {
  onAnalysisGenerated?: (analysisId: string) => void;
}

export const ToolCallHandlersContext = createContext<ToolCallHandlers | undefined>(undefined);

// Context for controlling autoRun behavior - historical threads should not autoRun
export const ChartAutoRunContext = createContext<boolean>(true);

// Placeholder component that shows analysis info before loading
const ChartPlaceholder: FC<{
  analysisId: string;
  onLoad: () => void;
}> = ({ analysisId, onLoad }) => {
  const analysis = useAnalysis(analysisId);
  const ChartIcon = getChartIcon(analysis.data?.visualization_type);

  return (
    <div className="aui-chart-placeholder">
      <div className="aui-chart-placeholder-content">
        <ChartIcon className="aui-chart-placeholder-icon" />
        {analysis.isLoading ? (
          <p className="text-muted mb-3">Loading chart info...</p>
        ) : analysis.data ? (
          <div className="text-center mb-3">
            <h5 className="mb-1">{analysis.data.name}</h5>
            <p className="text-muted mb-0">Chart Type: {analysis.data.visualization_type}</p>
          </div>
        ) : null}
        <button
          className="btn btn-primary d-inline-flex align-items-center"
          onClick={onLoad}
        >
          <PlayIcon className="me-2" style={{ width: 16, height: 16 }} />
          Load Chart
        </button>
      </div>
    </div>
  );
};

// Custom tool component for chart generation tools
const ChartToolCallComponent: FC<{
  toolName: string;
  argsText: string;
  result?: string;
  status?: { type: "running" | "complete" | "incomplete" };
}> = ({ toolName, argsText, result, status }) => {
  const handlers = useContext(ToolCallHandlersContext);
  const autoRunFromContext = useContext(ChartAutoRunContext);
  const isRunning = status?.type === "running" || (status === undefined && result === undefined);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isLoaded, setIsLoaded] = useState(autoRunFromContext);

  // Parse the result to get analysis_id
  let analysisId: string | null = null;
  if (result) {
    try {
      const parsed = typeof result === 'string' ? JSON.parse(result) : result;
      analysisId = parsed.analysis_id;
    } catch (e) {
      // Ignore parse errors
    }
  }

  // Fetch analysis data to get the chart name (React Query dedupes this with other useAnalysis calls)
  const analysis = useAnalysis(analysisId || '');
  const chartName = analysis.data?.name;

  const handleLoadChart = () => {
    setIsLoaded(true);
  };

  return (
    <div className={`aui-tool-fallback-root ${isRunning ? 'aui-tool-fallback-running' : ''}`}>
      <div className="aui-tool-fallback-header">
        {isRunning ? (
          <Loader2Icon className="aui-tool-fallback-icon aui-tool-fallback-icon-running" />
        ) : (
          <CheckCircle2Icon className="aui-tool-fallback-icon" style={{ color: 'var(--ct-success)' }} />
        )}
        <p className="aui-tool-fallback-title">
          {isRunning ? 'Thinking…' : `Created chart: ${chartName || 'Loading...'}`}
        </p>
        {!isRunning && analysisId && isLoaded && (
          <button
            className="btn btn-sm btn-outline-secondary ms-auto d-inline-flex align-items-center"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <>
                <ChevronUpIcon className="me-1" style={{ width: 14, height: 14 }} />
                Hide
              </>
            ) : (
              <>
                <ChevronDownIcon className="me-1" style={{ width: 14, height: 14 }} />
                Show
              </>
            )}
          </button>
        )}
      </div>
      {!isRunning && analysisId && isExpanded && (
        <div className="aui-chart-tool-content mt-3">
          {isLoaded ? (
            <AnalysisRunner
              analysisId={analysisId}
              autoRun
            />
          ) : (
            <ChartPlaceholder analysisId={analysisId} onLoad={handleLoadChart} />
          )}
        </div>
      )}
    </div>
  );
};

// Type assertion to match assistant-ui's expected type
export const ChartToolCall = ChartToolCallComponent as React.ComponentType<any>;
